<template>
  <div v-if="isLoading" class="loading-state" :class="containerClass">
    <!-- Card Grid Skeleton Loading -->
    <div v-if="type === 'cards'" class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
      <div v-for="i in cardCount" :key="i" class="animate-pulse">
        <div class="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-6">
          <div class="h-4 bg-gray-200 dark:bg-gray-600 rounded w-3/4 mb-4"></div>
          <div class="h-3 bg-gray-200 dark:bg-gray-600 rounded w-1/2 mb-2"></div>
          <div class="h-3 bg-gray-200 dark:bg-gray-600 rounded w-full mb-4"></div>
          <div class="h-8 bg-gray-200 dark:bg-gray-600 rounded w-1/3"></div>
        </div>
      </div>
    </div>

    <!-- Simple Spinner Loading -->
    <div v-else-if="type === 'spinner'" class="flex justify-center items-center py-8">
      <div class="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      <span v-if="text" class="ml-3 text-primary-text dark:text-primary-dark-text">{{ text || defaultText }}</span>
    </div>

    <!-- Compact Spinner (smaller version) -->
    <div v-else-if="type === 'compact'" class="flex justify-center items-center py-4">
      <div class="animate-spin rounded-full h-6 w-6 border-b-2 border-primary"></div>
      <span v-if="text" class="ml-2 text-sm text-primary-text dark:text-primary-dark-text">{{ text || defaultText }}</span>
    </div>

    <!-- Inline Spinner (for buttons or inline elements) -->
    <div v-else-if="type === 'inline'" class="flex items-center justify-center">
      <div class="animate-spin rounded-full h-4 w-4 border-b-2 border-current"></div>
      <span v-if="text" class="ml-2 text-sm">{{ text || defaultText }}</span>
    </div>

    <!-- Table Rows Skeleton -->
    <div v-else-if="type === 'table'" class="space-y-3">
      <div v-for="i in tableRows" :key="i" class="animate-pulse">
        <div class="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-4">
          <div class="grid grid-cols-4 gap-4">
            <div class="h-4 bg-gray-200 dark:bg-gray-600 rounded"></div>
            <div class="h-4 bg-gray-200 dark:bg-gray-600 rounded"></div>
            <div class="h-4 bg-gray-200 dark:bg-gray-600 rounded"></div>
            <div class="h-4 bg-gray-200 dark:bg-gray-600 rounded w-1/2"></div>
          </div>
        </div>
      </div>
    </div>

    <!-- List Items Skeleton -->
    <div v-else-if="type === 'list'" class="space-y-4">
      <div v-for="i in listItems" :key="i" class="animate-pulse">
        <div class="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-4">
          <div class="flex items-center space-x-4">
            <div class="rounded-full bg-gray-200 dark:bg-gray-600 h-12 w-12"></div>
            <div class="flex-1 space-y-2">
              <div class="h-4 bg-gray-200 dark:bg-gray-600 rounded w-3/4"></div>
              <div class="h-3 bg-gray-200 dark:bg-gray-600 rounded w-1/2"></div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Stats Cards Skeleton -->
    <div v-else-if="type === 'stats'" class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
      <div v-for="i in statsCount" :key="i" class="animate-pulse">
        <div class="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-6">
          <div class="flex items-center">
            <div class="rounded-md bg-gray-200 dark:bg-gray-600 h-12 w-12 mr-4"></div>
            <div class="flex-1">
              <div class="h-6 bg-gray-200 dark:bg-gray-600 rounded w-16 mb-2"></div>
              <div class="h-4 bg-gray-200 dark:bg-gray-600 rounded w-24"></div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Dashboard Skeleton -->
    <div v-else-if="type === 'dashboard'" class="space-y-6">
      <!-- Top stats -->
      <div class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
        <div v-for="i in statsCount" :key="`dash-stat-${i}`" class="animate-pulse">
          <div class="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-6">
            <div class="flex items-center">
              <div class="rounded-md bg-gray-200 dark:bg-gray-600 h-12 w-12 mr-4"></div>
              <div class="flex-1">
                <div class="h-6 bg-gray-200 dark:bg-gray-600 rounded w-20 mb-2"></div>
                <div class="h-5 bg-gray-200 dark:bg-gray-600 rounded w-24"></div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Main dashboard grid: large chart + side column -->
      <div class="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <!-- Chart / main content (span 2 cols on lg) -->
        <div class="lg:col-span-2 animate-pulse">
          <div class="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-6 h-72 mb-6"></div>

          <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div class="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-6 h-28 animate-pulse"></div>
            <div class="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-6 h-28 animate-pulse"></div>
          </div>
        </div>

        <!-- Right column: recent activity / calendar -->
        <div class="space-y-6">
          <div class="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-4 animate-pulse">
            <div class="h-4 bg-gray-200 dark:bg-gray-600 rounded w-1/3 mb-3"></div>
            <div class="space-y-3">
              <div v-for="i in 4" :key="`act-${i}`" class="flex items-center space-x-3">
                <div class="h-10 w-10 rounded-full bg-gray-200 dark:bg-gray-600"></div>
                <div class="flex-1">
                  <div class="h-3 bg-gray-200 dark:bg-gray-600 rounded w-3/4 mb-2"></div>
                  <div class="h-2 bg-gray-200 dark:bg-gray-600 rounded w-1/2"></div>
                </div>
              </div>
            </div>
          </div>

          <div class="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-4 h-40 animate-pulse">
            <!-- small calendar / rotation placeholder -->
            <div class="h-6 bg-gray-200 dark:bg-gray-600 rounded w-1/4 mb-4"></div>
            <div class="grid grid-cols-7 gap-2">
              <div v-for="i in 21" :key="`cal-${i}`" class="h-6 bg-gray-200 dark:bg-gray-600 rounded"></div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Custom Skeleton -->
    <div v-else-if="type === 'custom'" class="animate-pulse">
      <slot name="skeleton">
        <!-- Default custom skeleton -->
        <div class="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6">
          <div class="space-y-4">
            <div class="h-6 bg-gray-200 dark:bg-gray-600 rounded w-1/4"></div>
            <div class="h-4 bg-gray-200 dark:bg-gray-600 rounded w-full"></div>
            <div class="h-4 bg-gray-200 dark:bg-gray-600 rounded w-3/4"></div>
          </div>
        </div>
      </slot>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue'

export interface LoadingStateProps {
  isLoading: boolean
  type?: 'cards' | 'spinner' | 'compact' | 'inline' | 'table' | 'list' | 'stats' | 'custom' | 'dashboard'
  text?: string
  cardCount?: number
  tableRows?: number
  listItems?: number
  statsCount?: number
  containerClass?: string
}

const props = withDefaults(defineProps<LoadingStateProps>(), {
  isLoading: false,
  type: 'spinner',
  text: '',
  cardCount: 6,
  tableRows: 5,
  listItems: 5,
  statsCount: 4,
  containerClass: ''
})

// Default texts for different types
const defaultText = computed(() => {
  switch (props.type) {
    case 'cards':
      return ''
    case 'dashboard':
      return ''
    case 'spinner':
      return 'Loading...'
    case 'compact':
      return 'Loading...'
    case 'inline':
      return ''
    case 'table':
      return ''
    case 'list':
      return ''
    case 'stats':
      return ''
    case 'custom':
      return ''
    default:
      return 'Loading...'
  }
})
</script>

<style scoped>
.loading-state {
  width: 100%;
}

/* Enhanced pulse animation for better visual feedback */
@keyframes pulse {
  0%, 100% {
    opacity: 1;
  }
  50% {
    opacity: 0.5;
  }
}

.animate-pulse {
  animation: pulse 2s cubic-bezier(0.4, 0, 0.6, 1) infinite;
}

/* Smooth spinner animation */
@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

.animate-spin {
  animation: spin 1s linear infinite;
}
</style>